import { z } from 'zod'

/**
 * Relatório executivo (F9). O PDF é gerado de uma simulação concluída e carimba versões,
 * premissas e cobertura; a publicação no Portal tem limiar de qualidade.
 */

/** Abaixo disso a base é incompleta demais para o cliente levar o número embora (§17, riscos). */
export const LIMIAR_MESES_PUBLICACAO = 6

/**
 * Os três documentos que saem da mesma simulação (ADR 0024):
 *   TECNICO — o relatório completo, para o escritório;
 *   RESUMO  — três páginas na língua do cliente: a resposta, o preço e os próximos passos;
 *   CARTA   — uma página para o cliente encaminhar a quem compra dele.
 */
export const TIPOS_RELATORIO = ['TECNICO', 'RESUMO', 'CARTA', 'CONTRATO'] as const
export type TipoRelatorio = (typeof TIPOS_RELATORIO)[number]
export const ROTULO_TIPO_RELATORIO: Record<TipoRelatorio, string> = {
  TECNICO: 'Relatório técnico',
  RESUMO: 'Resumo para o cliente',
  CARTA: 'Carta ao comprador',
  CONTRATO: 'Estudo do contrato',
}
/** Nome do arquivo que o navegador oferece ao baixar. */
export const ARQUIVO_RELATORIO: Record<TipoRelatorio, string> = {
  TECNICO: 'via-tax-relatorio-tecnico',
  RESUMO: 'via-tax-resumo-cliente',
  CARTA: 'via-tax-carta-ao-cliente',
  CONTRATO: 'via-tax-estudo-do-contrato',
}

export const STATUS_RELATORIO = ['PROCESSANDO', 'CONCLUIDO', 'ERRO'] as const
export const STATUS_PORTAL_RELATORIO = ['NAO_PUBLICADO', 'PUBLICANDO', 'PUBLICADO', 'ERRO'] as const

/** Resumo que vai junto do PDF para o cartão do Portal: só números já carimbados no relatório. */
export const ResumoRelatorioPortal = z.object({
  empresa: z.string(),
  cnpj: z.string(),
  regime: z.string(),
  ano: z.number().int(),
  cargaHoje: z.number().int(),
  cargaAno: z.number().int(),
  variacao: z.number().int(),
  variacaoSobreReceitaPpm: z.number().int(),
  faixa: z.object({ otimista: z.number().int(), conservador: z.number().int() }),
  aliquotas: z.object({ cbs: z.number().int(), ibs: z.number().int(), status: z.enum(['ESTIMATIVA', 'OFICIAL']) }),
  simples: z
    .object({
      recomendacao: z.enum(['HIBRIDO', 'PURO']),
      diferenca: z.number().int(),
      estavel: z.boolean(),
      guiasPuro: z.number().int().nullable().optional(),
      guiasHibrido: z.number().int().nullable().optional(),
      cancelamentoAte: z.string().nullable().optional(),
    })
    .nullable(),
  mesesBase: z.number().int(),
  calculadoEm: z.string(),
  versoes: z.object({ engine: z.string(), parametros: z.string(), kb: z.string() }),
})
export type ResumoRelatorioPortal = z.infer<typeof ResumoRelatorioPortal>

export const PublicacaoPortal = z.object({
  relatorio_id: z.string().uuid(),
  raiz: z.string().regex(/^\d{8}$/),
  titulo: z.string(),
  gerado_em: z.string(),
  sha256: z.string().length(64),
  resumo: ResumoRelatorioPortal,
  pdf_b64: z.string(),
})
export type PublicacaoPortal = z.infer<typeof PublicacaoPortal>

/**
 * Retrato da operação no ano-base para o início do relatório: para quem a empresa vende e de quem
 * compra, com o regime de cada um. Pessoa física aparece só agregada, nunca por nome.
 */
export interface ParteOperacao {
  nome: string
  cnpj: string | null
  regime: string
  confirmado: boolean
  valor: number
  notas: number
}
export interface ContextoOperacao {
  competencias: string[]
  vendas: { total: number; porDestinacao: Array<{ destinacao: string; valor: number; clientes: number }>; maiores: Array<ParteOperacao & { destinacao: string }> }
  compras: { total: number; porRegime: Array<{ regime: string; valor: number; fornecedores: number }>; maiores: ParteOperacao[] }
}

// ─── Estudo do contrato: o cálculo de UMA contraparte, para o PDF (ADR 0029) ────────────────────

const Dinheiro = z.number().int()
const TributosEstudo = z.object({ icms: Dinheiro, ipi: Dinheiro, pis: Dinheiro, cofins: Dinheiro, iss: Dinheiro })
const SimplesEstudo = z.object({ dasPuro: Dinheiro, dasHibrido: Dinheiro, debitoHibrido: Dinheiro, creditoHibrido: Dinheiro })
const LinhaEstudo = z.object({
  notasSaida: z.number().int(),
  notasEntrada: z.number().int(),
  vendas: Dinheiro,
  compras: Dinheiro,
  tributosHoje: TributosEstudo,
  debitoCbsIbs: Dinheiro,
  creditoCbsIbs: Dinheiro,
  liquido: Dinheiro,
  simples: SimplesEstudo.nullable(),
})
export const MesEstudoContrato = LinhaEstudo.extend({ competencia: z.string() })
export type MesEstudoContrato = z.infer<typeof MesEstudoContrato>

const PontoPrecoSchema = z.object({ preco: Dinheiro, tributo: Dinheiro, nota: Dinheiro, custoCliente: Dinheiro, sobra: Dinheiro })
/** A faixa de preço como o motor a devolve; o PDF refaz as frases com `montarRepasse` para não haver duas versões. */
export const ZonaPrecoSchema = z.object({
  receitaHoje: Dinheiro,
  sobraPuro: Dinheiro,
  custoClientePuro: Dinheiro,
  custoClienteHoje: z.object({ lucroReal: Dinheiro, presumido: Dinheiro }),
  pisCofinsNaoCumulativo: z.number().int(),
  piso: PontoPrecoSchema,
  teto: PontoPrecoSchema,
  sugerido: PontoPrecoSchema,
  existe: z.boolean(),
  semCredito: z.object({ receitaHoje: Dinheiro, sobraPuro: Dinheiro, sobraMesmaNota: Dinheiro }).nullable(),
  exportacao: z.object({ receitaHoje: Dinheiro, sobraPuro: Dinheiro, sobraHibrido: Dinheiro }).nullable(),
  ganhoAnual: z.object({ piso: Dinheiro, sugerido: Dinheiro, teto: Dinheiro }),
  decisao: z.enum(['HIBRIDO', 'PURO']),
})

/**
 * O que fica gravado no relatório do tipo CONTRATO: o cálculo daquela contraparte no momento em que foi
 * pedido. O PDF sai daqui e não recalcula — assim o documento é imutável, como o relatório técnico.
 */
export const DadosEstudoContrato = z.object({
  contraparte: z.object({ nome: z.string().nullable(), documento: z.string().nullable(), regime: z.string(), confirmacao: z.string() }),
  papel: z.enum(['CLIENTE', 'FORNECEDOR']),
  ano: z.number().int(),
  periodo: z.object({ de: z.string().nullable(), ate: z.string().nullable(), meses: z.number().int() }),
  aliquotas: z.object({ cbs: z.number().int(), ibs: z.number().int(), status: z.enum(['ESTIMATIVA', 'OFICIAL']) }),
  regimeEmpresa: z.string(),
  /** empresa do Simples: crédito e débito desta relação só existem se ela optar pelo híbrido */
  creditoNoHibrido: z.boolean(),
  meses: z.array(MesEstudoContrato),
  totais: LinhaEstudo,
  /** receita bruta da empresa no período, de onde sai a faixa do Simples (não só deste contrato) */
  receitaBrutaEmpresa: Dinheiro.nullable(),
  zona: ZonaPrecoSchema.nullable(),
  creditoComprasNaConta: Dinheiro,
  avisos: z.array(z.string()),
  calculadoEm: z.string(),
  versoes: z.object({ engine: z.string(), parametros: z.string(), kb: z.string() }),
})
export type DadosEstudoContrato = z.infer<typeof DadosEstudoContrato>
